import React, { FC, useEffect, useMemo } from 'react';
import { useRequest } from 'ahooks';
import { Form, Input, Modal, Spin, message } from 'udesk-ui';
import { ModalProps } from 'antd';
import { postIntegrationJd } from 'src/api/integration/jd';
import { getIntegrationJdById, putIntegrationJdById } from 'src/api/integration/jd/{id}';
import { IntegrationConfigCreateRequest } from 'src/api/types';
import UdeskLocales from 'UdeskLocales';

export interface TemplateProps extends ModalProps {
    shopId?: number;
    mode?: 'create' | 'edit';
    onSaveSuccess?: () => void;
}

type FormModel = Pick<IntegrationConfigCreateRequest, 'shopName' | 'shopId' | 'userName'>;

const Template: FC<TemplateProps> = (props) => {
    const { shopId, onSaveSuccess, mode, ...restModalProps } = props;

    const { isCreateAndAuth, isEdit } = useMemo(() => {
        return {
            isCreateAndAuth: mode === 'create',
            isEdit: mode === 'edit',
        };
    }, [mode]);

    const title = useMemo(
        () =>
            isCreateAndAuth
                ? /* 新建店铺 */ UdeskLocales['current'].pages.systemIntegration.jdShop.components
                      .configForm.index.newStore
                : isEdit
                ? /* 编辑基础信息 */ UdeskLocales['current'].pages.systemIntegration.jdShop
                      .components.configForm.index.editBasicInformation
                : '',
        [isCreateAndAuth, isEdit]
    );

    const [form] = Form.useForm<FormModel>();

    const { loading: getLoading } = useRequest(
        () => getIntegrationJdById({ segments: { id: shopId! } }),
        {
            ready: restModalProps.visible && !!shopId,
            refreshDeps: [shopId],
            onSuccess: (res) => {
                form.setFieldsValue({
                    shopName: res.data?.shopName,
                    shopId: res.data?.shopId,
                    userName: res.data?.userName,
                });
            },
        }
    );

    const { loading: authShopConfigLoading, run: saveShopConfig } = useRequest(
        (values) => postIntegrationJd(values),
        {
            manual: true,
            onSuccess: (res) => {
                console.log('res', res);
                message.success(
                    /* 保存成功 */ UdeskLocales['current'].pages.systemIntegration.jdShop.components
                        .configForm.index.saveSuccessfully
                );
                onSaveSuccess?.();
            },
        }
    );

    const { loading: putShopConfigLoading, run: putShopConfig } = useRequest(
        (values) => putIntegrationJdById(values, { segments: { id: shopId! } }),
        {
            manual: true,
            ready: !!shopId,
            onSuccess: () => {
                message.success(
                    /* 保存成功 */ UdeskLocales['current'].pages.systemIntegration.jdShop.components
                        .configForm.index.saveSuccessfully
                );
                onSaveSuccess?.();
            },
        }
    );

    const onOk = () => {
        form.validateFields().then((values) => {
            if (isCreateAndAuth) {
                saveShopConfig(values);
            } else if (isEdit) {
                putShopConfig(values);
            }
        });
    };

    useEffect(() => {
        if (!restModalProps.visible) {
            form.setFieldsValue({
                shopName: undefined,
                shopId: undefined,
                userName: undefined,
            });
        }
    }, [form, restModalProps.visible]);

    const spinning = getLoading || authShopConfigLoading || putShopConfigLoading;

    const saveLoading = authShopConfigLoading || putShopConfigLoading;

    return (
        <Modal
            title={title}
            okText={
                /* 保存 */ UdeskLocales['current'].pages.systemIntegration.jdShop.components
                    .configForm.index.save
            }
            okButtonProps={{ loading: saveLoading, disabled: saveLoading }}
            onOk={onOk}
            destroyOnClose
            {...restModalProps}
        >
            <Spin spinning={spinning}>
                <Form form={form} labelCol={{ span: 6 }} wrapperCol={{ span: 18 }}>
                    <Form.Item
                        label={
                            /* 店铺名称 */ UdeskLocales['current'].pages.systemIntegration.jdShop
                                .components.configForm.index.shopName
                        }
                        name="shopName"
                        rules={[{ required: true }]}
                    >
                        <Input
                            placeholder={
                                /* 请输入 */ UdeskLocales['current'].pages.systemIntegration.jdShop
                                    .components.configForm.index.pleaseEnter
                            }
                            maxLength={200}
                        />
                    </Form.Item>

                    <Form.Item
                        label={
                            /* 商家ID */ UdeskLocales['current'].pages.systemIntegration.jdShop
                                .components.configForm.index.merchantID
                        }
                        name="shopId"
                        rules={[{ required: true }]}
                    >
                        <Input
                            placeholder={
                                /* 请输入 */ UdeskLocales['current'].pages.systemIntegration.jdShop
                                    .components.configForm.index.pleaseEnter
                            }
                            maxLength={200}
                        />
                    </Form.Item>

                    <Form.Item
                        label={
                            /* 账号 */ UdeskLocales['current'].pages.systemIntegration.jdShop
                                .components.configForm.index.accountNumber
                        }
                        name="userName"
                        rules={[{ required: true }]}
                    >
                        <Input
                            placeholder={
                                /* 请输入 */ UdeskLocales['current'].pages.systemIntegration.jdShop
                                    .components.configForm.index.pleaseEnter
                            }
                            maxLength={200}
                        />
                    </Form.Item>
                </Form>
            </Spin>
        </Modal>
    );
};

export default Template;
